import dayjs from "dayjs";
import { get } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCall,
  MdOutlineHealthAndSafety,
  MdOutlineLocationOn,
  MdOutlinePerson,
} from "react-icons/md";
import Input from "shared/components/Input";
import PhoneWithPrefix from "shared/components/Input/PhoneWithPrefix";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_PHONE_PREFIXES,
  ACORIS_PROFILES,
  ACORIS_SOCIAL_REGIMES,
  DATE_FORMAT,
  GENDER,
} from "utils/constants";
import {
  acorisAdultBirthDateDisabledDate,
  isAcorisAdultBirthDateAllowed,
} from "utils/helpers/date";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import { mapSocialRegimeOptions } from "utils/helpers/socialRegime";

export const step = { ADHERENT: "ADHERENT" };

const Adherent = ({
  index,
  translation,
  values,
  setFieldValue,
  ...formProps
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_ACORIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const PROFILE_TYPES_TRANSLATION = t("B2C_ACORIS.PROFILE_TYPES", {
    returnObjects: true,
  });
  const form = translation.TEMPLATES.CONTACT.STEPS[step.ADHERENT].FORM;
  const birthDateFieldProps = bindDateInputProps({
    name: "contact.user.birthDate",
    values,
    setFieldValue,
    ...formProps,
  });

  const regimeOptions = useMemo(
    () =>
      mapSocialRegimeOptions(ACORIS_SOCIAL_REGIMES, SOCIAL_REGIMES_TRANSLATION),
    [SOCIAL_REGIMES_TRANSLATION],
  );

  const profileOptions = useMemo(
    () =>
      Object.entries(ACORIS_PROFILES).map(([key, value]) => ({
        label: PROFILE_TYPES_TRANSLATION[key] || value,
        value,
      })),
    [PROFILE_TYPES_TRANSLATION],
  );

  return (
    <div className="contact-stepper" key={step.ADHERENT}>
      <div className="contact-stepper__header">
        <div className="contact-stepper__header--counter">
          <span>{index}</span>
        </div>
        <div className="contact-stepper__header--content">
          <h1 className="contact-stepper__header--content--title">
            <span>
              {translation.TEMPLATES.CONTACT.STEPS[step.ADHERENT].TITLE}
            </span>
          </h1>
          <p className="contact-stepper__header--content--description">
            {translation.TEMPLATES.CONTACT.STEPS[step.ADHERENT].DESCRIPTION}
          </p>
        </div>
      </div>
      <div className="contact-stepper__content">
        <Select
          required
          icon={<MdOutlinePerson size={22} />}
          options={profileOptions}
          placeholder={form.PROFILE?.PLACEHOLDER}
          label={form.PROFILE?.LABEL}
          {...bindInputProps({
            name: "contact.fields.profile",
            values,
            setFieldValue,
            ...formProps,
          })}
        />
        <Input
          type="radio"
          variant="group"
          required
          label={form.SEXE.LABEL}
          values={[
            {
              label: form.SEXE.MALE,
              value: GENDER.MALE,
            },
            {
              label: form.SEXE.FEMALE,
              value: GENDER.FEMALE,
            },
          ]}
          icon={<MdOutlineBadge size={22} />}
          {...bindInputProps({
            name: "contact.user.gender",
            values,
            ...formProps,
          })}
        />
        <Input
          required
          label={form.LASTNAME.LABEL}
          icon={<MdOutlineBadge size={22} />}
          placeholder={form.LASTNAME.PLACEHOLDER}
          {...bindTextFieldInputProps({
            name: "contact.user.lastname",
            values,
            setFieldValue,
            ...formProps,
          })}
        />
        <Input
          required
          label={form.FIRSTNAME.LABEL}
          icon={<MdOutlineBadge size={22} />}
          placeholder={form.FIRSTNAME.PLACEHOLDER}
          {...bindTextFieldInputProps({
            name: "contact.user.firstname",
            values,
            setFieldValue,
            ...formProps,
          })}
        />
        <Input
          required
          icon={<MdOutlineCake size={22} />}
          label={form.BIRTHDAY.LABEL}
          placeholder={form.BIRTHDAY.PLACEHOLDER}
          type="date"
          variant="simple"
          format={DATE_FORMAT}
          disabledDate={acorisAdultBirthDateDisabledDate}
          {...(!get(values, "contact.user.birthDate") && {
            defaultPickerValue: dayjs().subtract(30, "years"),
          })}
          {...birthDateFieldProps}
          onChange={(date) => {
            if (!date || isAcorisAdultBirthDateAllowed(date)) {
              birthDateFieldProps.onChange(date);
            }
          }}
          onBlur={(event) => {
            birthDateFieldProps.onBlur(event);
            const parsedDate = dayjs(event?.target?.value, DATE_FORMAT, true);
            if (isAcorisAdultBirthDateAllowed(parsedDate)) {
              setFieldValue(
                "contact.user.birthDate",
                parsedDate.set("hour", 4).set("minute", 0).set("second", 0),
              );
            }
          }}
        />
        <Input
          required
          icon={<MdOutlineLocationOn size={22} />}
          placeholder={form.ZIPCODE.PLACEHOLDER}
          label={form.ZIPCODE.LABEL}
          {...bindInputProps({
            name: "zipCode.value",
            values,
            ...formProps,
          })}
        />
        <Select
          required
          icon={<MdOutlineHealthAndSafety size={22} />}
          options={regimeOptions}
          placeholder={form.SOCIAL_REGIME.PLACEHOLDER}
          label={form.SOCIAL_REGIME.LABEL}
          {...bindInputProps({
            name: "contact.socialRegime",
            values,
            ...formProps,
          })}
        />
        <PhoneWithPrefix
          required
          label={form.PHONE.LABEL}
          icon={<MdOutlineCall size={16} />}
          placeholder={form.PHONE.PLACEHOLDER}
          prefixOptions={ACORIS_PHONE_PREFIXES}
          prefixValue={get(
            values,
            "phoneNumberPrefix.value",
            ACORIS_PHONE_PREFIXES[0].value,
          )}
          value={get(values, "phoneNumber.value", "")}
          errors={get(formProps, "errors.phoneNumber.value")}
          touched={get(formProps, "touched.phoneNumber.value")}
          onBlur={() => formProps.setFieldTouched("phoneNumber.value", true)}
          onChange={(localNumber) => {
            setFieldValue("phoneNumber.value", localNumber);
          }}
          onPrefixChange={(prefix) => {
            setFieldValue("phoneNumberPrefix.value", prefix);
            setFieldValue(
              "phoneNumber.value",
              get(values, "phoneNumber.value"),
              true,
            );
          }}
        />
        <Input
          type="switch"
          icon={<MdOutlinePerson size={22} />}
          label={form.ALREADY_MEMBER?.LABEL}
          checkedChildren={form.ALREADY_MEMBER?.OPTIONS?.YES}
          unCheckedChildren={form.ALREADY_MEMBER?.OPTIONS?.NO}
          checked={get(values, "contact.fields.isClient") === true}
          onChange={(value) => {
            setFieldValue("contact.fields.isClient", value);
          }}
        />
      </div>
    </div>
  );
};
export default Adherent;
